import React, { Component } from "react";
import PropTypes from "prop-types";
import axios from "axios";

import {
  LOCAL_STORAGE_NEWSLETTER_SIGNED_UP,
  REACT_APP_GOOGLE_RECAPTCHA_SITE_KEY,
} from "../store/const";

const pattern =
  /[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?/;
const requiredValidator = (val) => val && val !== "";
const emailValidator = (val) => val && pattern.test(val);

const FormField = ({
  name,
  id,
  type,
  value,
  ariaLabel,
  label,
  onChange,
  onFocus,
  hasFocus,
}) => {
  const fieldClassNames = `formField ${hasFocus ? "active" : ""}`;

  return (
    <div className={fieldClassNames}>
      <input
        className={value ? "hasValue" : ""}
        name={name}
        type={type}
        id={id}
        value={value}
        onChange={(event) => onChange(event, name)}
        onFocus={() => onFocus(name)}
        onBlur={() => onFocus(null)}
        aria-label={ariaLabel}
      />
      <label htmlFor={id} aria-hidden="true">
        {label}
      </label>
    </div>
  );
};

FormField.propTypes = {
  name: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  ariaLabel: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onFocus: PropTypes.func.isRequired,
  hasFocus: PropTypes.bool.isRequired,
};

export default class NewsletterSignupForm extends Component {
  static propTypes = {
    subscriptionIds: PropTypes.arrayOf(PropTypes.number).isRequired,
    consentId: PropTypes.number.isRequired,
    formName: PropTypes.string.isRequired,
    onSignUpSuccess: PropTypes.func,
    onInvalidForm: PropTypes.func,
    onValidForm: PropTypes.func,
  };

  constructor(props) {
    super(props);
    this.state = {
      emailSend: false,
      valid: false,
      submitted: false,
      email: "",
      name: "",
      subscriptionIds: props.subscriptionIds,
      consentId: props.consentId,
      captchaToken: "",
      captchaReady: false,
      loading: false,
    };
  }

  inputChangedHandler = (event, inputIdentifier) => {
    const updatedState = { ...this.state };
    updatedState[inputIdentifier] = event.target.value.toLowerCase();

    const isNameValid =
      inputIdentifier === "name"
        ? requiredValidator(updatedState.name.trim())
        : true;
    const isEmailValid =
      inputIdentifier === "email" ? emailValidator(updatedState.email) : true;
    updatedState.valid = isNameValid && isEmailValid;
  
    this.setState(updatedState, () => {
      if (this.state.valid && this.props.onValidForm) {
        this.props.onValidForm();
      }
    });
  };

  focusHandler = (name) => {
    this.setState({ focusElement: name });
  };

  checkCaptchaClient = (retries = 5) => {
    if (window.grecaptcha && window.grecaptcha.ready) {
      window.grecaptcha.ready(() => {
        this.setState({ captchaReady: true });
      });
    } else if (retries > 0) {
      setTimeout(() => this.checkCaptchaClient(retries - 1), 1000);
    }
  };

  handleCaptchaVerification = (callback) => {
    if (this.state.captchaReady && window.grecaptcha) {
      window.grecaptcha
        .execute(REACT_APP_GOOGLE_RECAPTCHA_SITE_KEY, { action: "submit" })
        .then((token) => {
          this.setState({ captchaToken: token }, callback);
        });
    }
  };

  signUpHandler = (event) => {
    event.preventDefault();
  
    const { email, name, valid } = this.state;
  
    if (!emailValidator(email)) {
      if (this.props.onInvalidForm) {
        this.props.onInvalidForm();
      }
      this.setState({ submitted: true });
      return;
    }
  
    if (!valid) {
      if (this.props.onInvalidForm) {
        this.props.onInvalidForm();
      }
      return;
    }
  
    this.setState({ submitted: true, loading: true });
  
    this.handleCaptchaVerification(() => {
      axios
        .post("/ajax/newsletter/signup", {
          name: name.trim(),
          email: email.trim(),
          subscriptionIds: this.state.subscriptionIds,
          consentId: this.state.consentId,
          recaptchaToken: this.state.captchaToken,
        })
        .then((response) => {
          if (response.data.success) {
            if (this.props.onSignUpSuccess) {
              this.props.onSignUpSuccess();
            }
            this.setState({ email: "", name: "", submitted: false });
          } else {
            console.error("CAPTCHA verification failed on server.");
          }
        })
        .catch((err) => {
          console.error("Error during signup:", err);
        })
        .finally(() => {
          this.setState({ loading: false });
        });
    });
  };

  componentDidMount() {
    const script = document.createElement("script");
    script.src = `https://www.google.com/recaptcha/api.js?render=${REACT_APP_GOOGLE_RECAPTCHA_SITE_KEY}`;
    script.async = true;
    script.onload = () => {
      this.checkCaptchaClient();
    };
    document.body.appendChild(script);
  }

  render() {
    const { focusElement, name, email, loading } = this.state;
    const { formName } = this.props;

    return (
      <div className="newsletterForm">
        <form name={formName} onSubmit={this.signUpHandler} noValidate>
          <FormField
            name="name"
            id={`${formName}-name`}
            type="text"
            aria-label="Indtast dit navn"
            value={name}
            label="Navn*"
            onChange={this.inputChangedHandler}
            onFocus={this.focusHandler}
            hasFocus={focusElement === "name"}
          />
          <FormField
            name="email"
            id={`${formName}-email`}
            type="email"
            aria-label="Indtast din e-mailadresse"
            value={email}
            label="E-mail*"
            onChange={this.inputChangedHandler}
            onFocus={this.focusHandler}
            hasFocus={focusElement === "email"}
          />
          <button
            id="signup-newsletter-btn"
            aria-label="Tilmeld dig nyhedsbrevet"
            type="submit"
            disabled={loading}
          >
            {loading ? "Sender..." : "Tilmeld"}
          </button>
        </form>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/NewsletterSignupForm.js